import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';

import type { ScriptStep } from '@shared/interfaces/assistantScripts';
import type { PollyLanguage } from '@shared/interfaces/polly';
import { languageFlags, languageLabels } from '../constants';
import { SectionLabelText, LanguageTab } from '../AssistantScriptDialog.styled';
import { useModalTheme } from '@shared/components/AssistantEditorModals/context';
import { modalThemeTokens } from '@shared/components/AssistantEditorModals/theme';
import {
  LanguageSection,
  LanguageTabsLeft,
  LangCompleteBadge,
  LangIncompleteBadge,
  AutoTranslateButton,
} from './styled';

const isLanguageComplete = (
  steps: ScriptStep[],
  lang: PollyLanguage,
): boolean =>
  steps.length > 0 &&
  steps.every((s) => {
    const t = s.translations?.[lang];
    return typeof t === 'string' && t.trim().length > 0;
  });

const renderLangBadge = (
  complete: boolean,
  translatedCount: number,
  totalCount: number,
  showIncompleteCount = true,
) => {
  if (totalCount === 0) {
    return null;
  }

  if (complete) {
    return <LangCompleteBadge>✓</LangCompleteBadge>;
  }

  if (!showIncompleteCount) {
    return null;
  }

  return (
    <LangIncompleteBadge>
      {translatedCount}/{totalCount}
    </LangIncompleteBadge>
  );
};

interface LanguageTabsProps {
  scriptLanguages: PollyLanguage[];
  effectiveBaseLanguage: PollyLanguage;
  activeLanguage: PollyLanguage;
  scriptSteps: ScriptStep[];
  // 'steps' translates/tracks the script step texts; 'button' translates/tracks
  // only the ui_click "Take a tour" button label.
  scope?: 'steps' | 'button';
  uiClickButtonLabel: string;
  uiClickButtonLabelTranslations: Partial<Record<PollyLanguage, string>>;
  nonBaseLangs: PollyLanguage[];
  isTranslating: boolean;
  translateMenuAnchor: HTMLElement | null;
  onActiveLanguageChange: (lang: PollyLanguage) => void;
  onTranslateAll: () => void;
  onTranslateMenuOpen: (anchor: HTMLElement) => void;
  onTranslateMenuClose: () => void;
  onPickLanguages: () => void;
}

const LanguageTabs = ({
  scriptLanguages,
  effectiveBaseLanguage,
  activeLanguage,
  scriptSteps,
  scope = 'steps',
  uiClickButtonLabel,
  uiClickButtonLabelTranslations,
  nonBaseLangs,
  isTranslating,
  translateMenuAnchor,
  onActiveLanguageChange,
  onTranslateAll,
  onTranslateMenuOpen,
  onTranslateMenuClose,
  onPickLanguages,
}: LanguageTabsProps) => {
  const { t } = useTranslation('common');
  const isButtonScope = scope === 'button';
  const modalTheme = useModalTheme();
  const tokens = modalThemeTokens[modalTheme];
  const translateDisabled =
    isTranslating ||
    (isButtonScope
      ? uiClickButtonLabel.trim().length === 0
      : !scriptSteps.some((s) => s.text.trim()));

  return (
    <LanguageSection>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <SectionLabelText sx={{ mb: 0 }}>{t('assistant.scriptDialog.language')}</SectionLabelText>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          <AutoTranslateButton
            variant="contained"
            size="small"
            sx={{
              borderRadius: '8px 0 0 8px !important',
              borderRight: 'none !important',
            }}
            onClick={onTranslateAll}
            disabled={translateDisabled}
            startIcon={
              isTranslating ? (
                <CircularProgress size={13} color="inherit" />
              ) : undefined
            }
          >
            {isTranslating
              ? t('assistant.scriptDialog.translating')
              : t('assistant.scriptDialog.autoTranslateAll')}
          </AutoTranslateButton>
          <AutoTranslateButton
            variant="contained"
            size="small"
            sx={{
              borderRadius: '0 8px 8px 0 !important',
              px: '6px !important',
              minWidth: 'unset',
            }}
            onClick={(e) => onTranslateMenuOpen(e.currentTarget)}
            disabled={translateDisabled}
          >
            <ArrowDropDownIcon sx={{ fontSize: 18 }} />
          </AutoTranslateButton>
        </Box>
        <Menu
          anchorEl={translateMenuAnchor}
          open={Boolean(translateMenuAnchor)}
          onClose={onTranslateMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                mt: 0.5,
                minWidth: 190,
                background: tokens.surface,
                border: tokens.inlineBtnBorder,
                '& .MuiMenuItem-root': {
                  color: tokens.inlineBtnColor,
                  '&:hover': { backgroundColor: tokens.inlineBtnHoverBg },
                },
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              onTranslateMenuClose();
              onPickLanguages();
            }}
            sx={{ gap: 1.5, alignItems: 'flex-start', py: 1 }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: tokens.menuAccentColor,
              }}
            >
              {t('assistant.scriptDialog.pickLanguages')}
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
      <LanguageTabsLeft>
        {scriptLanguages.map((lang) => {
          const isBase = lang === effectiveBaseLanguage;
          const active = activeLanguage === lang;
          let complete: boolean;
          let translatedCount: number;
          let totalCount: number;

          if (isButtonScope) {
            // The button label is a single translatable item per language.
            totalCount = 1;
            complete = isBase
              ? uiClickButtonLabel.trim().length > 0
              : (uiClickButtonLabelTranslations[lang] ?? '').trim().length > 0;
            translatedCount = complete ? 1 : 0;
          } else {
            totalCount = scriptSteps.length;

            if (isBase) {
              complete =
                totalCount > 0 &&
                scriptSteps.every((s) => s.text.trim().length > 0);
              translatedCount = scriptSteps.filter(
                (s) => s.text.trim().length > 0,
              ).length;
            } else {
              complete = isLanguageComplete(scriptSteps, lang);
              translatedCount = scriptSteps.filter((s) => {
                const t = s.translations?.[lang];
                return typeof t === 'string' && t.trim().length > 0;
              }).length;
            }
          }

          return (
            <LanguageTab
              key={lang}
              active={active}
              complete={complete}
              onClick={() => onActiveLanguageChange(lang)}
            >
              <span>{languageFlags[lang]}</span>
              <span>{languageLabels[lang]}</span>
              {isBase && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '9px',
                    fontWeight: 700,
                    px: 0.5,
                    py: 0.1,
                    borderRadius: 0.75,
                    bgcolor: 'var(--am-icon-accent)',
                    color: 'var(--am-primary-btn-color)',
                    lineHeight: 1.4,
                  }}
                >
                  {t('assistant.scriptDialog.baseBadge')}
                </Typography>
              )}
              {renderLangBadge(
                complete,
                translatedCount,
                totalCount,
                !isButtonScope,
              )}
            </LanguageTab>
          );
        })}
      </LanguageTabsLeft>
    </LanguageSection>
  );
};

export default LanguageTabs;
